import moment from 'moment';
import { stringify } from 'querystring';
import * as stations from 'constants/directEurostarStations';
import { noPassengers, searchTypes } from 'pages/BookingMagnet/BookingMagnet.constants';
import { buildPassengersList } from 'pages/JourneySearch/CheapestFaresSearch.utils';
import { CLASSES_OF_SERVICE_BY_TYPE, STANDARD, WC_COMP } from 'constants/classOfService';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { RETURN_JOURNEY } from 'types/cheapestFares';
import { isLoungeStaff } from 'utils/user';

const stationCodes = [
  'LONDON',
  'PARIS',
  'LILLE',
  'DISNEY',
  'BRUSSEL',
  'AMSTERDAM',
  'ROTERDAM',
  'SCHIPHOL',
  'ANTWERPEN',
  'LIEGE',
  'AACHEN',
  'COLOGNE',
  'DORTMUND',
  'DUISBURG',
  'DUSSELDORF',
  'ESSEN'
].map((code) => stations[`${code}_UIC`]);

export const canUseLowestFareFinder = (user) =>
  !user.isReadOnly &&
  !isLoungeStaff(user) &&
  !!(user.canHaveStandardBookings || user.canHaveGroupBookings || user.canHaveAccessibleTravelBookings);
export const getDirectStations = (allStations) => allStations.filter(({ uic }) => stationCodes.includes(uic));

export const getDefaultSearch = (user, directStations) => {
  const stationFor = (code) =>
    directStations.find(({ shortCode, uic }) => uic === code || (code && shortCode?.replace(/^STN_/, '') === code.replace(/^STN_/, '')))
      ?.uic || '';
  return {
    journeyType: 'return',
    origin: stationFor(user.defaultJourneyOrigin),
    destination: stationFor(user.defaultJourneyDestination),
    outboundMonth: moment().format('YYYY-MM'),
    inboundMonth: moment().format('YYYY-MM'),
    outboundClass: STANDARD,
    inboundClass: STANDARD,
    accessible: false,
    wheelchairType: 'adult',
    companion: false,
    adults: 1,
    seniors: 0,
    children: 0,
    youths: 0,
    currency: user.paymentCurrencies?.[0] || '',
    fareType: productFamilyCodes.PUB
  };
};

export const getPassengers = (form) => ({
  ...noPassengers,
  ...(form.accessible
    ? {
        adultsWheelchair: form.wheelchairType === 'adult' ? 1 : 0,
        childrenWheelchair: form.wheelchairType === 'child' ? 1 : 0,
        wheelchairCompanions: form.companion ? 1 : 0
      }
    : { adults: form.adults, seniors: form.seniors, children: form.children, youths: form.youths })
});

export const isValidSearch = (form) => {
  const total = buildPassengersList(getPassengers(form)).length;
  const currentMonth = moment().format('YYYY-MM');
  return (
    !!form.origin &&
    !!form.destination &&
    form.origin !== form.destination &&
    !!form.currency &&
    total > 0 &&
    total <= 9 &&
    form.outboundMonth >= currentMonth &&
    (form.journeyType === 'single' || form.inboundMonth >= form.outboundMonth)
  );
};

export const buildCalendarRequest = (form, direction) => {
  const month = moment(form[`${direction}Month`], 'YYYY-MM');
  const startDate = moment.max(month.clone().startOf('month'), moment().startOf('day')).format('YYYY-MM-DD');
  // S3 calendar ranges exclude the end date, so include the month's final day
  // by using the first day of the next month as the boundary.
  const endDate = month.clone().add(1, 'month').startOf('month').format('YYYY-MM-DD');
  const isOutbound = direction === 'outbound';
  const isReturn = form.journeyType === 'return';
  const classProductFamily = `${form.fareType}_${form[`${direction}Class`]}`;
  return {
    currency: form.currency,
    passengersList: buildPassengersList(getPassengers(form)),
    isTotalPriceSearch: true,
    productFamiliesSearch: form.accessible ? [productFamilyCodes.PUB] : [classProductFamily, `RED_${classProductFamily}`],
    cheapestFaresLists: [
      {
        direction: direction.toUpperCase(),
        origin: isOutbound ? form.origin : form.destination,
        destination: isOutbound ? form.destination : form.origin,
        startDate,
        endDate,
        ...(isReturn && {
          journeyType: RETURN_JOURNEY,
          oppositeDate: isOutbound
            ? moment(endDate).add(1, 'day').format('YYYY-MM-DD')
            : moment(startDate).subtract(1, 'day').format('YYYY-MM-DD')
        })
      }
    ]
  };
};

export const buildJourneySearchUrl = (form, selected, directStations) =>
  `/journeys?${stringify({
    ...getPassengers(form),
    origin: form.origin,
    destination: form.destination,
    originStationName: directStations.find(({ uic }) => uic === form.origin)?.name,
    destinationStationName: directStations.find(({ uic }) => uic === form.destination)?.name,
    currency: form.currency,
    outboundDate: selected.outbound.date,
    ...(form.journeyType === 'return' && { inboundDate: selected.inbound.date }),
    productFamilies: form.accessible ? productFamilyCodes.PUB : form.fareType,
    bookingTypeSelected: form.accessible ? searchTypes.WITH_DISABILITY : searchTypes.STANDARD,
    maxTransfers: 0,
    lowestFareFinder: 'true',
    outboundLowestPrice: selected.outbound.price,
    outboundLowestClass: form.accessible ? WC_COMP : form.outboundClass,
    ...(form.journeyType === 'return' && {
      inboundLowestPrice: selected.inbound.price,
      inboundLowestClass: form.accessible ? WC_COMP : form.inboundClass
    })
  })}`;

export const groupJourneysByLowestFare = (journeys, { price, classOfService }) => {
  const classes = CLASSES_OF_SERVICE_BY_TYPE[classOfService] || [classOfService];
  const sorted = [...journeys].sort((a, b) => a.timing.departs.localeCompare(b.timing.departs));
  const matches = (journey) =>
    journey.fares.some(
      (fare) =>
        classes.includes(fare.classOfService?.code) &&
        fare.canBeBooked &&
        fare.bookingItems?.length > 0 &&
        Math.round(fare.bookingItems.reduce((total, item) => total + item.price, 0) * 100) === Math.round(price * 100)
    );
  return { morningJourneys: sorted.filter(matches), afternoonJourneys: sorted.filter((journey) => !matches(journey)) };
};
